import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import CopyButton from "@components/buttons/CopyButton";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { transactionApiServices } from "@services/apis/Transactions";
import { ColDef } from "ag-grid-community";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import ConstantFunctions from "../../../utils/constants_functions";
import PrimeDetailsActionButton from "../../Prime/components/PrimeDetailsActionButton";

interface CPGLogsDetailsProps {
  transactionId: number;
}

interface CallbackLogData {
  id: number;
  eventId: number;
  request: unknown;
  response: unknown;
  createdAt: string;
}

const constantFunctions = new ConstantFunctions();

const CPGLogsDetails: React.FC<CPGLogsDetailsProps> = ({ transactionId }) => {
  const [logsData, setLogsData] = useState<CallbackLogData[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [jsonDialogOpen, setJsonDialogOpen] = useState<boolean>(false);
  const [selectedJson, setSelectedJson] = useState<string>("");
  const [dialogTitle, setDialogTitle] = useState<string>("");

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(false);
      const response = await transactionApiServices.getCPGCallbackLogs({
        transactionId,
      });
      setLogsData(response.data || []);
    } catch (err) {
      console.error("Failed to fetch CPG callback logs:", err);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [transactionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleJsonClick = useCallback((data: unknown, title: string) => {
    let formattedJson = "";

    try {
      formattedJson =
        typeof data === "string"
          ? JSON.stringify(JSON.parse(data), null, 2)
          : JSON.stringify(data, null, 2);
    } catch {
      formattedJson = String(data ?? "");
    }

    setSelectedJson(formattedJson);
    setDialogTitle(title);
    setJsonDialogOpen(true);
  }, []);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date & Time",
        field: "createdAt",
        flex: 1.4,
        minWidth: 120,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
        cellRenderer: (params: any) => {
          if (!params.value) {
            return (
              <span className="text-[10px] text-text-base font-medium">
                - -
              </span>
            );
          }

          return (
            <div className="flex flex-col justify-center leading-tight">
              <span className="text-[10px] text-text-base font-medium">
                {constantFunctions.formatDate(params.value)}
              </span>
              <span className="text-[10px] text-text-secondary">
                {constantFunctions.timeFormatInHHMMSS(params.value)}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Req",
        field: "request",
        flex: 0.85,
        minWidth: 70,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          return (
            <div
              className="flex items-center h-full"
              onClick={(event) => event.stopPropagation()}
            >
              <PrimeDetailsActionButton
                label="View"
                icon="data_object"
                skipBusy
                onClick={() =>
                  handleJsonClick(
                    params.data.request,
                    `Request - Transaction ${transactionId}`,
                  )
                }
              />
            </div>
          );
        },
      },
      {
        headerName: "Res",
        field: "response",
        flex: 0.85,
        minWidth: 70,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          return (
            <div
              className="flex items-center h-full"
              onClick={(event) => event.stopPropagation()}
            >
              <PrimeDetailsActionButton
                label="View"
                icon="data_object"
                skipBusy
                onClick={() =>
                  handleJsonClick(
                    params.data.response,
                    `Response - Transaction ${transactionId}`,
                  )
                }
              />
            </div>
          );
        },
      },
    ],
    [handleJsonClick],
  );

  if (error) {
    return (
      <div className="flex flex-col gap-[12px] mt-[20px]">
        <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Callback Logs:
          </p>
          <span className="text-text-base text-[12px] font-normal">
            Fetch error
          </span>
          <PrimeDetailsActionButton
            label="Try again"
            icon="refresh"
            onClick={fetchData}
            skipBusy
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="border border-border-subtle rounded-md bg-bg-base mt-[20px]">
        <Skeleton height={100} />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-[12px] mt-[20px] overflow-auto"
      style={{ height: "65dvh" }}
    >
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          Callback Logs:
        </p>
        <div style={{ height: "400px", width: "100%" }}>
          {logsData.length > 0 ? (
            <AgGridReactTable
              rowData={logsData}
              columnDefs={columnDefs}
              getRowId={(params) => `log-${params.data.id}`}
              compact={true}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <span className="text-text-base text-[12px] font-normal">
                No data available
              </span>
            </div>
          )}
        </div>
      </div>

      <SlidingPanel
        isOpen={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        heading={dialogTitle}
        isSlider={true}
        widthDesktop={"34%"}
        showCross={false}
      >
        <div className="h-full flex flex-col pt-16">
          <div className="flex items-center justify-end px-[12px] py-[8px]">
            <CopyButton
              copyText={selectedJson}
              copyMessage="JSON copied!"
              buttonSize="28px"
              copyIconSize={14}
            />
          </div>
          <div className="flex-1 overflow-hidden px-[12px] pb-[12px]">
            <pre className="h-full bg-bg-base border border-border-subtle rounded-lg p-[12px] text-[12px] leading-relaxed text-text-base overflow-auto whitespace-pre-wrap break-all font-mono">
              {selectedJson}
            </pre>
          </div>
        </div>
      </SlidingPanel>
    </div>
  );
};

export default CPGLogsDetails;
